/***
 *
 *   USERS
 *   Manage and monitor all users registered to your application
 *
 **********/

import { useState, useMemo, useContext, useEffect, useRef } from 'react';
import { parseLicenses, splitDateTime, convertToLocalTime } from '../utility/helper';
import { buildWebappUrl, webappCallsEnabled } from '../utility/webapp';
import { ViewContext, AuthContext, Animate, useAPI, useDebounce, LicenseBadge, MetricBadge, PracticeAreaTags, DataTable } from 'components/lib';
import { useNavigate } from 'react-router-dom';
import Style from './users.tailwind.js';
import { LICENSE_TIERS, FILTER_DEFAULTS, countActiveFilters, COLS, PAGE_SIZES, STRINGS } from './users.constants.js';

// ─── Filter panel sub-components ──────────────────────────────────────────────

function FilterSectionLabel({ children }) {
  return (
    <div className={Style.filterSectionLbl}>
      {children}
    </div>
  );
}

function RangeInputs({ minVal, maxVal, onMinChange, onMaxChange, placeholder }) {
  return (
    <div className={Style.rangeWrap}>
      <input
        type="number"
        min={0}
        placeholder={`Min${placeholder ? ' ' + placeholder : ''}`}
        value={minVal}
        onChange={e => onMinChange(e.target.value)}
        className={Style.rangeInput}
      />
      <span className={Style.rangeSeparator}>–</span>
      <input
        type="number"
        min={0}
        placeholder={`Max${placeholder ? ' ' + placeholder : ''}`}
        value={maxVal}
        onChange={e => onMaxChange(e.target.value)}
        className={Style.rangeInput}
      />
    </div>
  );
}

function FiltersPanel({ draft, setDraft, onApply, onClear }) {
  function toggleTier(tier) {
    setDraft(prev => {
      const has = prev.licenseTiers.includes(tier);
      return {
        ...prev,
        licenseTiers: has
          ? prev.licenseTiers.filter(t => t !== tier)
          : [...prev.licenseTiers, tier],
      };
    });
  }

  return (
    <div className={Style.filterPanel}>
      {/* License tier */}
      <div className={Style.filterSection}>
        <FilterSectionLabel>{STRINGS.licenseSectionLabel}</FilterSectionLabel>
        <div className={Style.tierChips}>
          {LICENSE_TIERS.map(tier => {
            const isActive = draft.licenseTiers.includes(tier);
            return (
              <button
                key={tier}
                onClick={() => toggleTier(tier)}
                className={`${Style.tierBtnBase} ${isActive ? Style.tierBtnActive : Style.tierBtnInactive}`}
              >
                {tier}
              </button>
            );
          })}
        </div>
      </div>

      {/* Latest prompts/mo */}
      <div className={Style.filterSection}>
        <FilterSectionLabel>{STRINGS.promptsSectionLabel}</FilterSectionLabel>
        <RangeInputs
          minVal={draft.promptsMin}
          maxVal={draft.promptsMax}
          onMinChange={v => setDraft(p => ({ ...p, promptsMin: v }))}
          onMaxChange={v => setDraft(p => ({ ...p, promptsMax: v }))}
        />
      </div>

      {/* Date created */}
      <div className={Style.filterSection}>
        <FilterSectionLabel>{STRINGS.dateSectionLabel}</FilterSectionLabel>
        <div className={Style.dateCol}>
          <div className={Style.dateRow}>
            <span className={Style.dateFromToLabel}>{STRINGS.dateFrom}</span>
            <input
              type="date"
              value={draft.dateFrom}
              onChange={e => setDraft(p => ({ ...p, dateFrom: e.target.value }))}
              className={Style.dateInput}
            />
          </div>
          <div className={Style.dateRow}>
            <span className={Style.dateFromToLabel}>{STRINGS.dateTo}</span>
            <input
              type="date"
              value={draft.dateTo}
              onChange={e => setDraft(p => ({ ...p, dateTo: e.target.value }))}
              className={Style.dateInput}
            />
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className={Style.filterFooter}>
        <button onClick={onClear} className={Style.filterClearBtn}>
          {STRINGS.clearAll}
        </button>
        <button onClick={onApply} className={Style.filterApplyBtn}>
          {STRINGS.apply}
        </button>
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

function openInNewTab(url) {
  window.open(url, '_blank', 'noopener,noreferrer');
}

export function Users() {
  const viewContext = useContext(ViewContext);
  const authContext = useContext(AuthContext);
  const navigate    = useNavigate();

  const [search,      setSearch]      = useState('');
  const [sortKey,     setSortKey]     = useState('name');
  const [sortDir,     setSortDir]     = useState('asc');
  const [page,        setPage]        = useState(1);
  const [pageSize,    setPageSize]    = useState(50);
  const [openKebab,   setOpenKebab]   = useState(null);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const [draftFilters,   setDraftFilters]   = useState(FILTER_DEFAULTS);
  const [appliedFilters, setAppliedFilters] = useState(FILTER_DEFAULTS);
  const [refreshKey,     setRefreshKey]     = useState(0);

  const debouncedSearch = useDebounce(search, 800);

  const filtersRef  = useRef(null);
  const activeCount = countActiveFilters(appliedFilters);

  // Build the server-side query URL from all state
  const usersQuery = (() => {
    const params = new URLSearchParams();
    params.set('offset', (page - 1) * pageSize);
    params.set('limit', pageSize);
    params.set('sort', sortKey);
    params.set('order', sortDir);
    if (debouncedSearch.trim())                  params.set('search', debouncedSearch.trim());
    if (appliedFilters.licenseTiers.length > 0) params.set('license_tiers', appliedFilters.licenseTiers.join(','));
    if (appliedFilters.promptsMin !== '')        params.set('prompts_min', appliedFilters.promptsMin);
    if (appliedFilters.promptsMax !== '')        params.set('prompts_max', appliedFilters.promptsMax);
    if (appliedFilters.dateFrom)                params.set('date_from', appliedFilters.dateFrom);
    if (appliedFilters.dateTo)                  params.set('date_to', appliedFilters.dateTo);
    if (refreshKey > 0)                         params.set('_t', refreshKey);
    return `/api/user?${params.toString()}`;
  })();

  const users = useAPI(usersQuery);

  const rawRows = useMemo(() => users.data?.results || [], [users.data]);
  const total   = users.data?.total || 0;

  // Reset to page 1 whenever filters/sort/search change
  useEffect(() => { setPage(1); }, [debouncedSearch, sortKey, sortDir, pageSize, appliedFilters]);

  // Close filter panel on outside click
  useEffect(() => {
    if (!filtersOpen) return;
    function handler(e) {
      if (filtersRef.current && !filtersRef.current.contains(e.target)) setFiltersOpen(false);
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [filtersOpen]);

  function openFilters() {
    setDraftFilters(appliedFilters);
    setFiltersOpen(true);
  }

  function handleApply() {
    setAppliedFilters(draftFilters);
    setFiltersOpen(false);
  }

  function handleClear() {
    setDraftFilters(FILTER_DEFAULTS);
    setAppliedFilters(FILTER_DEFAULTS);
    setFiltersOpen(false);
  }

  const pageRows = useMemo(() => {
    return rawRows.map(u => {
      const { tiers, practiceAreas } = parseLicenses(u.assigned_licenses);
      return {
        ...u,
        _date_created_str: convertToLocalTime(u.date_created) || '',
        license:           tiers,
        practice_areas:    practiceAreas,
      };
    });
  }, [rawRows]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  function toggleSort(key) {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  }

  function handleAccountTdClick(e) {
    e.stopPropagation();
  }

  function handleAccountClick(e, accountId) {
    e.preventDefault();
    if (e.ctrlKey || e.metaKey) openInNewTab(`/accounts/${accountId}`);
    else navigate(`/accounts/${accountId}`);
  }

  function handleAccountAuxClick(e, accountId) {
    if (e.button === 1) {
      e.preventDefault();
      openInNewTab(`/accounts/${accountId}`);
    }
  }

  function deleteUser(u) {
    viewContext.modal.show({
      title: STRINGS.deleteModalTitle,
      form: {},
      buttonText: STRINGS.deleteModalBtn,
      text: `Are you sure you want to delete ${u.email}? This will remove them from all accounts.`,
      url: buildWebappUrl(authContext.remote_server, `/api/user/${u.id}`),
      method: 'DELETE',
      destructive: true,
      audit: { action: 'user.deleted', target_type: 'user', target_id: u.id },
    }, () => {
      viewContext.notification.show(`${u.email} was deleted`, 'success', true);
      setRefreshKey(k => k + 1);
    });
  }

  // Close kebab on outside click
  useEffect(() => {
    if (openKebab === null) return;
    function handler() { setOpenKebab(null); }
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [openKebab]);

  const loading = users.loading;

  return (
    <Animate>
      <h1 className={Style.pageTitle}>{STRINGS.pageTitle}</h1>
      <p className={Style.pageSubtitle}>{STRINGS.pageSubtitle}</p>

      {/* Search + Filters bar */}
      <div className={Style.topBar}>
        <div className={Style.searchWrap}>
          <span className={Style.searchIcon}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"/>
              <line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
          </span>
          <input
            type="text"
            placeholder={STRINGS.searchPlaceholder}
            value={search}
            onChange={e => setSearch(e.target.value)}
            className={Style.searchInput}
          />
        </div>

        {/* Filters button + panel */}
        <div ref={filtersRef} className={Style.filterBtnWrap}>
          <button
            onClick={() => filtersOpen ? setFiltersOpen(false) : openFilters()}
            className={`${Style.filterBtnBase} ${activeCount > 0 ? Style.filterBtnActive : Style.filterBtnInactive}`}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="4" y1="6" x2="20" y2="6"/>
              <line x1="8" y1="12" x2="16" y2="12"/>
              <line x1="11" y1="18" x2="13" y2="18"/>
            </svg>
            {activeCount > 0 ? `${STRINGS.filtersBtn} · ${activeCount}` : STRINGS.filtersBtn}
          </button>

          {filtersOpen && (
            <FiltersPanel
              draft={draftFilters}
              setDraft={setDraftFilters}
              onApply={handleApply}
              onClear={handleClear}
            />
          )}
        </div>
      </div>

      {/* Table */}
      <DataTable
        columns={COLS}
        rows={pageRows}
        loading={loading}
        emptyMessage={STRINGS.emptyMessage}
        sortKey={sortKey}
        sortDir={sortDir}
        onSort={toggleSort}
        page={page}
        pageSize={pageSize}
        totalPages={totalPages}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        pageSizes={PAGE_SIZES}
        onRowClick={(u, e) => {
          const url = `/users/${u.id}`;
          if (e.ctrlKey || e.metaKey) openInNewTab(url);
          else navigate(url);
        }}
        onRowAuxClick={(u) => openInNewTab(`/users/${u.id}`)}
        renderCell={(key, _value, u) => {
          if (key === 'name') return (
            <td key="name" className={Style.cellName}>
              <a href={`/users/${u.id}`} onClick={e => e.preventDefault()} className={Style.cellNameLink}>
                {u.name || '—'}
              </a>
            </td>
          );
          if (key === 'email') return (
            <td key="email" className={Style.cellEmail}>{u.email || '—'}</td>
          );
          if (key === 'account_name') return (
            <td key="account_name" className={Style.cellAccount} onClick={handleAccountTdClick}>
              {u.account_name ? (
                <a
                  href={`/accounts/${u.default_account}`}
                  onClick={e => handleAccountClick(e, u.default_account)}
                  onAuxClick={e => handleAccountAuxClick(e, u.default_account)}
                  className={Style.cellAccountLink}
                >
                  {u.account_name}
                </a>
              ) : '—'}
            </td>
          );
          if (key === '_date_created_str') {
            const { date, time } = splitDateTime(u._date_created_str);
            return (
              <td key="_date_created_str" className={Style.cellDate}>
                <span className={Style.cellDateMain}>{date}</span>
                {time && <><br /><span className={Style.cellDateTime}>{time}</span></>}
              </td>
            );
          }
          if (key === 'license') return (
            <td key="license" className={Style.cellLicense}><LicenseBadge tiers={u.license} /></td>
          );
          if (key === 'practice_areas') return (
            <td key="practice_areas" className={Style.cellPractice}>
              <PracticeAreaTags areas={u.practice_areas} />
            </td>
          );
          if (key === 'latest_prompts_per_month') return (
            <td key="latest_prompts_per_month" className={Style.cellNumber}>
              {u.latest_prompts_per_month ?? 0}
            </td>
          );
          if (key === 'fingerprint_count') return (
            <td key="fingerprint_count" className={Style.cellNumber}>
              {u.fingerprint_count ?? 0}
            </td>
          );
          if (key === 'average_monthly_iPs') return (
            <td key="average_monthly_iPs" className={Style.cellCenter}>
              <MetricBadge value={u.average_monthly_iPs ?? 0} />
            </td>
          );
          if (key === 'average_monthly_devices') return (
            <td key="average_monthly_devices" className={Style.cellNumber}>
              {u.average_monthly_devices ?? 0}
            </td>
          );
          if (key === '_actions') return (
            <td key="_actions" className={Style.cellActions} onClick={e => e.stopPropagation()}>
              <button
                onClick={e => { e.stopPropagation(); setOpenKebab(openKebab === u.id ? null : u.id); }}
                className={Style.kebabBtn}
              >
                ⋮
              </button>
              {openKebab === u.id && (
                <div
                  onClick={e => e.stopPropagation()}
                  className={Style.kebabMenu}
                >
                  <button
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(u.email);
                        viewContext.notification.show(`Copied ${u.email}`, 'success', true);
                      } catch {
                        viewContext.notification.show(`Could not copy ${u.email}`, 'error', true);
                      }
                      setOpenKebab(null);
                    }}
                    className={`${Style.kebabItem} ${Style.kebabItemNormal}`}
                  >
                    {STRINGS.kebabMail}
                  </button>
                  {webappCallsEnabled() && <button
                    onClick={() => { deleteUser(u); setOpenKebab(null); }}
                    className={`${Style.kebabItem} ${Style.kebabItemDanger}`}
                  >
                    {STRINGS.kebabDelete}
                  </button>}
                </div>
              )}
            </td>
          );
          return null;
        }}
      />
    </Animate>
  );
}
